import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const movie = getMovieById(id || '');

  if (!movie) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 bg-background-200 rounded-2xl flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-200 font-heading mb-2">内容未找到</h2>
          <p className="text-foreground-500 text-sm mb-4">该影视内容可能已下架或不存在</p>
          <Link to="/" className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedMovies(movie.id, 4);

  const breadcrumbItems = [
    { '@type': 'ListItem' as const, position: 1, name: '首页', item: 'https://www.90yingshi.com' },
    { '@type': 'ListItem' as const, position: 2, name: movie.category, item: `https://www.90yingshi.com/category/${movie.categorySlug}` },
    { '@type': 'ListItem' as const, position: 3, name: movie.title, item: `https://www.90yingshi.com/article/${movie.id}` },
  ];

  return (
    <>
      <SEOHead
        title={`${movie.title} - ${movie.year}年${movie.category}高清在线观看`}
        description={`${movie.title}：${movie.description} ${movie.tags.join('、')}，导演${movie.director}，主演${movie.actors.join('、')}。90影视免费高清在线观看，不缓冲无广告。`}
        keywords={`${movie.title},${movie.category},在线观看,高清,免费,${movie.tags.join(',')}`}
        canonicalUrl={`https://www.90yingshi.com/article/${movie.id}`}
        ogType="article"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="Article"
        data={{
          headline: movie.title,
          description: movie.description,
          datePublished: movie.releaseDate,
          url: `https://www.90yingshi.com/article/${movie.id}`,
          articleSection: movie.category,
          keywords: movie.tags.join(','),
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${movie.title} | 90影视`,
          description: movie.description,
          url: `https://www.90yingshi.com/article/${movie.id}`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
          },
        }}
      />
      <SchemaOrg type="BreadcrumbList" data={{ items: breadcrumbItems }} />

      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="sticky top-20">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100 border border-background-200/70">
                  <img
                    src={movie.imageUrl}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Link
                    to={`/category/${movie.categorySlug}`}
                    className="text-xs bg-background-200/60 text-foreground-400 px-3 py-1 rounded-full hover:bg-primary-500/10 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {movie.category}
                  </Link>
                  {movie.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-background-200/60 text-foreground-500 px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-50 font-heading mb-4">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-400"></i>
                    <span className="text-accent-400 font-bold text-lg">{movie.rating}</span>
                    <span className="text-foreground-500 text-xs">/ 10</span>
                  </div>
                  <span className="text-foreground-700">|</span>
                  <span className="text-foreground-400 text-sm">{movie.year}</span>
                  <span className="text-foreground-700">|</span>
                  <span className="text-foreground-400 text-sm">{movie.region}</span>
                  <span className="text-foreground-700">|</span>
                  <span className="text-foreground-400 text-sm">{movie.duration}</span>
                  {movie.episodes && movie.totalEpisodes && (
                    <>
                      <span className="text-foreground-700">|</span>
                      <span className="text-foreground-400 text-sm">
                        {movie.episodes === movie.totalEpisodes ? `全${movie.totalEpisodes}集` : `更新至${movie.episodes}集/共${movie.totalEpisodes}集`}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 mb-6">
                <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-3">影片简介</h2>
                <p className="text-foreground-400 text-sm leading-relaxed">{movie.fullDescription}</p>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 mb-6">
                <h2 className="text-lg font-semibold text-foreground-200 font-heading mb-4">影片信息</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">导演：</span>
                    <span className="text-foreground-300 text-sm">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">主演：</span>
                    <span className="text-foreground-300 text-sm">{movie.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">地区：</span>
                    <span className="text-foreground-300 text-sm">{movie.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">时长：</span>
                    <span className="text-foreground-300 text-sm">{movie.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">年份：</span>
                    <span className="text-foreground-300 text-sm">{movie.year}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">类型：</span>
                    <span className="text-foreground-300 text-sm">{movie.tags.join(' / ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-600 text-sm w-16 flex-shrink-0">更新：</span>
                    <span className="text-foreground-300 text-sm">{movie.updateInfo}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-all duration-200 whitespace-nowrap text-sm cursor-pointer flex items-center justify-center gap-2">
                  <i className="ri-play-circle-line text-lg"></i>
                  立即播放
                </button>
                <button className="w-full sm:w-auto px-8 py-3 bg-background-100 hover:bg-background-200/80 text-foreground-300 font-medium rounded-lg border border-background-300/60 transition-all duration-200 whitespace-nowrap text-sm cursor-pointer flex items-center justify-center gap-2">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
                <button className="w-full sm:w-auto px-8 py-3 bg-background-100 hover:bg-background-200/80 text-foreground-300 font-medium rounded-lg border border-background-300/60 transition-all duration-200 whitespace-nowrap text-sm cursor-pointer flex items-center justify-center gap-2">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-14">
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-2">相关推荐</h2>
                <p className="text-foreground-500 text-sm">你可能也喜欢这些{movie.category}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group relative overflow-hidden rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[2/3] overflow-hidden">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 bg-background-950/80 backdrop-blur-sm rounded-md px-2 py-0.5 flex items-center gap-1">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        <span className="text-accent-400 text-xs font-semibold">{item.rating}</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-foreground-100 text-sm font-medium truncate font-heading group-hover:text-primary-400 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-foreground-600 text-xs mt-1">{item.year} · {item.tags[0]}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}